import type { CSSProperties } from "react";

/**
 * The palette set. See docs/design-system.md §2.
 *
 * A component never names a colour — it sets `data-palette` and references
 * roles (`--accent`, `--accent-fg`, …) defined in src/styles/palettes.css.
 * Adding an eleventh palette means editing that CSS file and this list;
 * no component changes.
 */
export const PALETTES = [
  "teal",
  "aqua",
  "sky",
  "cyan",
  "indigo",
  "violet",
  "grass",
  "sun",
  "coral",
  "berry",
] as const;

export type Palette = (typeof PALETTES)[number];

/** Palettes taken directly from the live academiq.ai brand stylesheet. */
export const BRAND_PALETTES: readonly Palette[] = [
  "teal",
  "aqua",
  "sky",
  "cyan",
  "indigo",
  "violet",
  "grass",
];

/** Warm extensions — the brand catalogue is entirely cool-toned. */
export const EXTENSION_PALETTES: readonly Palette[] = ["sun", "coral", "berry"];

/** Deterministic palette pick, so the same key always gets the same colour. */
export function paletteFor(key: string): Palette {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTES[h % PALETTES.length] ?? "teal";
}

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** #abc → #aabbcc, so one parser handles both forms. */
function expand(hex: string): string {
  if (hex.length !== 4) return hex;
  const [r, g, b] = [hex[1], hex[2], hex[3]];
  return `#${r}${r}${g}${g}${b}${b}`;
}

/**
 * White or the brand ink, whichever is legible ON this colour.
 *
 * The same judgement --accent-fg encodes per palette in palettes.css, made at
 * run time because a colour typed into the console was not there to be checked.
 * sRGB relative luminance, WCAG's own formula; 0.55 is where white and ink swap
 * for the mid-tones this actually meets.
 */
function inkOn(hex: string): string {
  const value = parseInt(expand(hex).slice(1), 16);
  const channel = (shift: number) => {
    const c = ((value >> shift) & 255) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
  return luminance > 0.55 ? "#1b1b2f" : "#ffffff";
}

export function accentVars(
  color: string | null | undefined,
): CSSProperties | undefined {
  const hex = color?.trim() ?? "";
  if (!HEX.test(hex)) return undefined;

  /* Cast because CSSProperties has no index signature — React has applied
     custom properties from a style object since 16.4, TypeScript just has no
     way to say so. */
  return {
    "--accent": hex,
    "--accent-fg": inkOn(hex),
    "--accent-soft": `color-mix(in srgb, ${hex} 12%, var(--surface))`,
    // Toward --text, which is dark in light mode and light in dark mode — so
    // this deepens or lifts the hue to stay readable on the wash above.
    "--accent-soft-fg": `color-mix(in srgb, ${hex} 62%, var(--text))`,
    "--accent-line": `color-mix(in srgb, ${hex} 24%, var(--surface))`,
    // Borders and focus rings need >= 3:1 against the surface, and a bright hex
    // does not clear that on its own. See --accent-edge in palettes.css.
    "--accent-edge": `color-mix(in srgb, ${hex} 85%, var(--text))`,
  } as CSSProperties;
}
